import { HttpErrorResponse } from '@angular/common/http';

/**
 * Field-keyed validation messages from a DRF 422 response.
 *
 * DRF returns 400 by default for validation; this codebase configures it to
 * 422 via the unified exception handler. Each field maps to one or more
 * messages.  Forms use `bindServerErrors()` to attach them to controls.
 *
 * Non-field errors live under the conventional 'non_field_errors' key.
 */
export type FieldErrors = Record<string, string[]>;

/**
 * Thrown by the error interceptor on 422 (validation) responses so feature
 * code can bind errors to form controls without the interceptor showing a
 * toast.  Other error classes propagate as plain HttpErrorResponse.
 */
export class ApiValidationError extends Error {
  override readonly name = 'ApiValidationError';

  constructor(
    public readonly fieldErrors: FieldErrors,
    public readonly status: number,
    public readonly originalResponse: HttpErrorResponse,
  ) {
    super('Validation failed');
  }

  /** Non-field, form-level error messages (no specific control to attach to). */
  get formErrors(): string[] {
    return this.fieldErrors['non_field_errors'] ?? [];
  }
}

/**
 * Coerce an arbitrary error response body into FieldErrors.  Accepts:
 *   - { field: ['msg'] }                 — DRF default
 *   - { field: 'msg' }                   — single string per field
 *   - { detail: 'msg' }                  — DRF generic
 *   - { non_field_errors: [...] }        — DRF non-field shape
 *
 * Anything else collapses into { non_field_errors: ['<stringified>'] }.
 */
export function parseFieldErrors(body: unknown): FieldErrors {
  if (!body || typeof body !== 'object') {
    return { non_field_errors: [String(body)] };
  }
  const obj = body as Record<string, unknown>;
  const out: FieldErrors = {};
  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      // DRF `many=True` nested serializers return an array of per-item error
      // OBJECTS (e.g. schedules: [{}, {"time_end": [...]}], guardians:
      // [{"mobile": [...]}]). Mapping each element through String() would yield
      // the useless "[object Object]"; instead JSON-stringify the whole array
      // so nested handlers (e.g. bindScheduleErrors) can JSON.parse it back.
      // Plain string arrays pass through unchanged as messages.
      const hasNestedObjects = value.some((v) => v !== null && typeof v === 'object');
      out[key] = hasNestedObjects ? [JSON.stringify(value)] : value.map((v) => String(v));
    } else if (typeof value === 'string') {
      out[key] = [value];
    } else if (value && typeof value === 'object') {
      // Nested errors (e.g. {translations: [{name: [...]}]}); flatten as JSON.
      out[key] = [JSON.stringify(value)];
    }
  }
  // DRF's `detail` is conventionally a plain message — promote to non_field_errors.
  if (out['detail'] && !out['non_field_errors']) {
    out['non_field_errors'] = out['detail'];
    delete out['detail'];
  }
  return out;
}
